/**
 * 多段ヘッダーの「分岐質問」見出しセル。条件を表示する帯の下、自分の深さの段に置き、最下段まで rowSpan で伸ばす。
 * 分岐質問が未追加の条件分岐では「分岐質問を追加」ボタンのプレースホルダを出す。
 */
import { colors } from '@kmns-internal/furikake'
import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Theme } from '@mui/material/styles'
import { TableActionMenu } from '../TableActionMenu'
import {
  HEADER_CELL_HEIGHT,
  columnWidthSx,
  headerStickyTop,
  isColumnInputType,
  renderColumnTypeIcon,
  type TableBranchColumn,
} from '../model'
import { LogicNumberChip } from './LogicNumberChip'
import { TableColumnHeaderName } from './TableColumnHeaderName'
import type { TableEditHandlers } from './TablePreview'

type BaseProps = {
  branch: TableBranchColumn
  /** 最下段まで伸ばす段数。 */
  rowSpan: number
  selected: boolean
  /** この分岐質問がさらに条件分岐を持つときの通し番号。同じ番号がその帯にも出る。 */
  logicNumber: number | undefined
}

type InteractiveProps = BaseProps & {
  interactive: true
  handlers: TableEditHandlers
}

type ReadonlyProps = BaseProps & {
  interactive?: false
}

type Props = InteractiveProps | ReadonlyProps

const branchCellSx = (theme: Theme, depth: number, selected: boolean) => ({
  position: 'sticky' as const,
  top: headerStickyTop(theme, depth),
  zIndex: 2,
  ...columnWidthSx(theme),
  height: HEADER_CELL_HEIGHT(theme),
  p: 0,
  borderRight: 1,
  borderBottom: 1,
  borderColor: 'divider',
  boxSizing: 'border-box' as const,
  verticalAlign: 'middle' as const,
  bgcolor: selected ? colors.primary.selected : colors.white,
})

export const TablePreviewBranchHeader = (props: Props) => {
  const { branch, rowSpan, selected, logicNumber } = props
  const depth = branch.conditions.length
  const question = branch.question

  // 分岐質問が未追加の条件分岐: 追加導線だけ出す。
  if (!question) {
    return (
      <Box
        component="th"
        rowSpan={rowSpan}
        sx={theme => branchCellSx(theme, depth, false)}
      >
        <Stack alignItems="center" justifyContent="center" sx={{ px: 1 }}>
          {props.interactive ? (
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => props.handlers.onAddBranchQuestion(branch.logicId)}
            >
              分岐質問を追加
            </Button>
          ) : (
            <Typography variant="body2" color="text.disabled" noWrap>
              分岐質問なし
            </Typography>
          )}
        </Stack>
      </Box>
    )
  }

  const responseType = question.question?.responseType
  const menuItems = props.interactive
    ? props.handlers.buildBranchMenuItems(branch)
    : []

  const content = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ width: '100%', minWidth: 0, px: 1.5, py: 0.75 }}
    >
      <Box
        sx={theme => ({
          flex: '0 0 auto',
          display: 'inline-flex',
          width: theme.spacing(3),
          height: theme.spacing(3),
        })}
      >
        {responseType != null &&
          isColumnInputType(responseType) &&
          renderColumnTypeIcon(responseType)}
      </Box>
      {props.interactive ? (
        <Box
          component="button"
          type="button"
          onClick={event => {
            event.stopPropagation()
            props.handlers.onSelectColumn(question.id)
          }}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          <TableColumnHeaderName
            name={question.question?.name?.trim() || '質問名未設定'}
          />
        </Box>
      ) : (
        <TableColumnHeaderName
          name={question.question?.name?.trim() || '質問名未設定'}
        />
      )}
      {logicNumber && <LogicNumberChip logicNumber={logicNumber} />}
      {props.interactive && menuItems.length > 0 && (
        <TableActionMenu items={menuItems} ariaLabel="分岐質問の操作" />
      )}
    </Stack>
  )

  return (
    <Box
      component="th"
      rowSpan={rowSpan}
      sx={theme => branchCellSx(theme, depth, selected)}
    >
      {content}
    </Box>
  )
}
